import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin, catchError, filter, map, switchMap, take } from 'rxjs';
import { CalAngularService } from '@cvx/cal-angular';
import { 
  AccessType, 
  UserContextState, 
  UserInfo, 
  INITIAL_USER_CONTEXT,
  meetsAccessRequirement
} from '../../models/authorization';
import { ConfigService } from './config.service';
import { UserPrivilegesService } from './user-privileges.service';

/**
 * Central service managing user authorization context
 * Responsible for:
 * - loading user roles and privileges from backend
 * - providing methods for checking access
 */
@Injectable({
  providedIn: 'root'
})
export class UserContextService {
  private userContextSubject = new BehaviorSubject<UserContextState>(INITIAL_USER_CONTEXT);
  public userRoleSubject = new BehaviorSubject<string[]>([]);
  /**
   * Observable stream of user context state
   */
  public userContext$: Observable<UserContextState> = this.userContextSubject.asObservable();

  constructor(
    private authService: CalAngularService,
    private configService: ConfigService,
    private userPrivilegesService: UserPrivilegesService
  ) {}

  /**
   * Initializes user context
   * Loads roles and privileges
   */
  public initialize(): Observable<UserContextState> {
    // 1. Check if user is signed in
    return this.authService.isUserSignedIn().pipe(
      switchMap(isSignedIn => {
        if (!isSignedIn) {
          const error = 'User is not signed in';
          console.error('UserContextService:', error);
          this.userContextSubject.next({
            ...INITIAL_USER_CONTEXT,
            isLoaded: true,
            errors: [error]
          });
          return of(this.userContextSubject.value);
        }

        // 2. Get user information
        const account = this.authService.getAccount();
        const userInfo: UserInfo = {
          id: account?.localAccountId || '',
          email: account?.username || '',
          displayName: account?.name || account?.username || ''
        };

        // 3. Load user roles and privileges from backend (single endpoint)
        return forkJoin({
          roles: this.userPrivilegesService.getUserRoles(),
          privileges: this.userPrivilegesService.getUserPrivileges()
        }).pipe(
          map(({ roles, privileges }) => {
            if (roles.length === 0) {
              const error = 'User has no assigned roles';
              console.warn('UserContextService:', error);
            }
            this.userRoleSubject.next(roles);

            const state: UserContextState = {
              isLoaded: true,
              user: userInfo,
              roles,
              privileges,
              errors: roles.length === 0 ? ['User has no assigned roles'] : []
            };

            this.userContextSubject.next(state);
            return state;
          }),
          catchError(error => {
            console.error('UserContextService: Error loading user context', error);
            const state: UserContextState = {
              isLoaded: true,
              user: userInfo,
              roles: [],
              privileges: [],
              errors: ['Failed to load user context: ' + error.message]
            };
            this.userContextSubject.next(state);
            return of(state);
          })
        );
      }),
      catchError(error => {
        console.error('UserContextService: Error checking sign-in status', error);
        const state: UserContextState = {
          ...INITIAL_USER_CONTEXT,
          isLoaded: true,
          errors: ['Failed to check authentication status: ' + error.message]
        };
        this.userContextSubject.next(state);
        return of(state);
      })
    );
  }

  /**
   * Returns current context state (synchronously)
   */
  public getCurrentContext(): UserContextState {
    return this.userContextSubject.value;
  }

  /**
   * Checks if user has required access level for a given permission
   * @param permissionKey - permission key (e.g. "Administration.Users.Page")
   * @param requiredAccess - required minimum access level
   * @returns true if user has required access
   */
  public hasAccess(permissionKey: string, requiredAccess: AccessType = AccessType.Read): boolean {
    const effectiveAccess = this.getEffectiveAccess(permissionKey);
    return meetsAccessRequirement(effectiveAccess, requiredAccess);
  }

  /**
   * Returns user's effective access level for a given permission
   * @param permissionKey - permission key
   * @returns access level (None if no permissions)
   */
  public getEffectiveAccess(permissionKey: string): AccessType {
    const privilege = this.userContextSubject.value.privileges.find(
      p => p.permissionKey === permissionKey
    );
    return privilege ? privilege.access : AccessType.None;
  }

  /**
   * Checks if context has been loaded
   */
  public isLoaded(): boolean {
    return this.userContextSubject.value.isLoaded;
  }

  /**
   * Waits for context to load
   * @returns Observable that emits when context is loaded
   */
  public waitForLoad(): Observable<UserContextState> {
    if (this.isLoaded()) {
      return of(this.userContextSubject.value);
    }
    
    return this.userContext$.pipe(
      filter(state => state.isLoaded),
      take(1)
    );
  }


  /**
   * Resets user context (e.g. after logout)
   */
  public reset(): void {
    this.userContextSubject.next(INITIAL_USER_CONTEXT);
  }
}
